<template>
  <b-card no-body class="border-0">
    <div id="tab-id-provider" :data-tab-id="tabId">{{ tabId }}</div>
    <b-row>
      <b-col>
        <b-select size="sm" :options="tripOptions" v-model="selectedTrip">
          <template #first>
            <b-select-option :value="null" disabled>Select a Trip</b-select-option>
          </template>
        </b-select>
      </b-col>
    </b-row>
    <b-row v-if="isTripSelected">
      <b-col class="p-2 lead mt-4 text-center" v-if="isDeclined">
        Travel has been declined for this registration. If you would like to undecline and submit a travel request, please click
        <b-link href="#" @click.prevent="undeclineTravel">here</b-link>.
      </b-col>
      <b-col class="p-2" v-else-if="!isBlocked">
        <b-tabs small v-model="tabIndex">
          <confirmation
            title="Summary"
            :navigable="tabVisible('summary')"
            :dorm-exception="dormException"
            :airfare-exception="airfareException"
            :trip="selectedTrip"
            v-if="!isNewTrip" />
          <traveler-information
            ref="travelerInformationTab"
            :navigable="tabVisible('traveler-information')"
            :traveler="(selectedTrip || {}).traveler"
            :disabled="!isEditable" />
          <travel-policy
            v-if="isNewTrip"
            @question-answered="questionAnswered"
            :navigable="tabVisible('travel-policy')"
            :policyAgreedTo="policyAgreedTo"
            :dormRoom="needsDormRoom"
            :airfare="needsAirfare" />
          <select-dorm-room
            ref="selectDormRoom"
            :navigable="tabVisible('dorm-room')"
            :trip="selectedTrip"
            :disabled="!isEditable"
            @dorm-stays-selected="dormStaysSelected"
            @reset="rollback(false, true, false)" />
          <select-airfare
            ref="selectAirfare"
            :navigable="tabVisible('airfare')"
            :trip="selectedTrip"
            :dorm-stays="dormStays"
            @reset="rollback(false, false, true)"
            @flight-requests-selected="flightRequestsSelected" />
          <confirmation
            :navigable="tabVisible('confirmation')"
            :dorm-exception="dormException"
            :airfare-exception="airfareException"
            :trip="selectedTrip"
            v-if="isNewTrip" />
        </b-tabs>
      </b-col>
      <b-col class="p-2 lead mt-4" v-else>
        Your Mentor{{ !!mentor ? ', ' + mentor.firstName + ' ' + mentor.lastName + ',' : '' }} is not yet registered for this Program. They must be
        registered before you can submit a travel request for this session.
      </b-col>
    </b-row>
    <b-row v-if="isEditable && !isBlocked">
      <b-col>
        <b-button size="sm" variant="info" @click="rollback"> Reset All </b-button>
      </b-col>
      <b-col class="text-right" v-if="isNewTrip">
        <b-button size="sm" variant="danger" v-if="buttonVisible('decline-travel')" @click="declineTravel">Decline Room & Airfare</b-button>
        <b-button size="sm" variant="primary" v-if="buttonVisible('previous')" @click="previous">Previous</b-button>
        <b-button size="sm" variant="success" v-if="buttonVisible('next')" @click="next">Next</b-button>
        <b-button size="sm" variant="success" v-if="buttonVisible('accept')" @click="acceptTravel">
          Accept {{ needsDormRoom && needsAirfare ? 'Room & Airfare' : needsDormRoom ? 'Room' : 'Airfare' }}
        </b-button>
      </b-col>
      <b-col class="text-right" v-else>
        <b-button size="sm" variant="success" @click="saveTrip" v-if="tabIndex > 0"> {{ saveButtonLabel }} </b-button>
        <b-button size="sm" variant="danger" @click="cancelTrip"> Cancel Trip </b-button>
      </b-col>
    </b-row>
  </b-card>
</template>
<script>
import _ from 'underscore';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { isAfter } from 'date-fns';
import { Trip, DormStay, FlightRequest } from '@/model/travel';
import { date, errorToastOptions, errorModalOptions } from '@/util/formatters';
import travelDao from '@/dao/travel_dao';
import userDao from '@/dao/user_dao';
import Breadcrumb from '@/views/menu/breadcrumb/breadcrumb';
import TravelerInformation from '@/views/private/travel/TravelerInformation.vue';
import TravelPolicy from '@/views/private/travel/TravelPolicy.vue';
import SelectDormRoom from '@/views/private/travel/SelectDormRoom.vue';
import SelectAirfare from '@/views/private/travel/SelectAirfare.vue';
import Confirmation from '@/views/private/travel/Confirmation.vue';

const now = new Date();

@Component({
  components: { TravelerInformation, TravelPolicy, SelectDormRoom, SelectAirfare, Confirmation }
})
export default class MyTravel extends Vue {
  @Prop({ type: Number, default: () => null }) registrationId;

  sTrip = null;
  sDormStays = [];
  sFlights = {};
  trips = [];

  tabIndex = 0;
  attendee = 0;
  registration = 0;
  policyAgreedTo = null;
  needsDormRoom = null;
  needsAirfare = null;
  mentor = null;

  get user() {
    return this.$store.getters['userSession/getUser'];
  }

  get selectedTrip() {
    return this.sTrip;
  }

  set selectedTrip(trip) {
    this.strip = null;
    this.sDormStays = [];
    this.sFlights = {};

    this.tabIndex = 0;

    this.$nextTick(() => {
      if (trip instanceof Trip) {
        this.sTrip = trip;
        this.sDormStays = trip.dormStays.map((ds) => ds.cloneTemplate());

        if (trip.flightRequests.to) {
          this.sFlights.to = trip.flightRequests.to.cloneTemplate();
        }

        if (trip.flightRequests.from) {
          this.sFlights.from = trip.flightRequests.from.cloneTemplate();
        }
      }
    });
  }

  get tripOptions() {
    return _(this.trips)
      .chain()
      .sortBy((t) => t.startDate)
      .map((t) => ({ text: this.getOptionText(t), value: t }))
      .value();
  }

  get isTripSelected() {
    return this.selectedTrip instanceof Trip;
  }

  get isNewTrip() {
    return true === (this.selectedTrip || {}).isNewTrip;
  }

  get isDeclined() {
    return this.isTripSelected && this.selectedTrip.declinedTravel;
  }

  get isEditable() {
    // First it has to be selected
    if (!this.isTripSelected) {
      return false;
    }

    // It cannot be declined
    if (this.selectedTrip.declinedTravel) {
      return false;
    }

    // It cannot be in the past
    if (isAfter(now, this.selectedTrip.endDate)) {
      return false;
    }

    // Airfare cannot have been booked
    if (true === this.selectedTrip.bookedAirfare) {
      return false;
    }

    return true;
  }

  get isBlocked() {
    return this.isTripSelected && this.selectedTrip.blocked;
  }

  get dormStays() {
    return (this.selectedTrip || {}).dormStays || [];
  }

  set dormStays(dormStays) {
    if (this.isTripSelected) {
      this.selectedTrip.dormStays = dormStays;
    }
  }

  get flightRequests() {
    return this.isTripSelected ? this.selectedTrip.flightRequests : {};
  }

  set flightRequests(flightRequests) {
    if (this.isTripSelected) {
      this.selectedTrip.flightRequests = flightRequests;
    }
  }

  get dormException() {
    return this.needsDormRoom && !!this.$refs.selectDormRoom && this.$refs.selectDormRoom.hasErrors() && this.user.isAnAdministrator();
  }

  get airfareException() {
    return this.needsAirfare && !!this.$refs.selectAirfare && !this.$refs.selectAirfare.isValidSelection && this.user.isAnAdministrator();
  }

  dormStaysSelected(dormStays) {
    this.dormStays = dormStays;
  }

  flightRequestsSelected(flightRequests) {
    this.flightRequests = flightRequests;
  }

  getOptionText(trip) {
    const base = `${trip.workshop} (${date(trip.startDate, 'M/d/yyyy')} - ${date(trip.endDate, 'M/d/yyyy')})`;

    const tripId = parseInt(trip.id, 10) || null;

    switch (true) {
      case trip.declinedTravel:
        return `${base} - Travel has been declined`;
      case null == tripId:
        return `${base}${isAfter(new Date(), trip.endDate) ? '' : ' - ' + (trip.blocked ? 'Cannot Request Travel Yet' : 'Pending')}`;
      default:
        return base;
    }
  }

  get saveButtonLabel() {
    switch (this.tabIndex) {
      case 1:
        return 'Save Traveler Info';
      case 2:
        return 'Save Dorm Rooms';
      case 3:
        return 'Save Airfare';
      default:
        return 'Unknown!';
    }
  }

  tabVisibleOnNew(tab) {
    switch (tab) {
      case 'traveler-information':
        return this.isTripSelected;

      case 'travel-policy':
        return this.isNewTrip;

      case 'dorm-room':
        return this.isTripSelected && this.policyAgreedTo && this.needsDormRoom && null !== this.needsAirfare;

      case 'airfare':
        return (
          this.isTripSelected && // Trip must be selected
          this.policyAgreedTo && // Travel policy
          null !== this.needsDormRoom && // The needs dorm room question must be answered
          this.needsAirfare && // The needs airfare question has been answered 'Yes'
          (!this.needsDormRoom || !_(this.dormStays).isEmpty())
        ); // If needs dorm room was answered 'Yes' then at least one
      // dorm stay must have been selected

      case 'confirmation':
        return (
          this.isTripSelected && // Trip must be selected
          this.policyAgreedTo && // Travel policy
          null !== this.needsDormRoom && // The needs dorm room question must be answered
          null !== this.needsAirfare && // The needs airfare question must be answered
          (!this.needsDormRoom || !_.isEmpty(this.dormStays)) && // If needs dorm room was answered 'Yes' then at least one
          // dorm stay must have been selected
          (!this.needsAirfare || !_(this.flightRequests).isEmpty()) && // If needs airfare was answered 'Yes' then at least one
          // flight has been selected
          (!this.needsAirfare || this.selectedTrip.traveler.workspace.confirmed)
        ); // If needs airfare was answered 'Yes' then
      // The travelers's name/dob/gender must be confirmed

      case 'summary':
      default:
        return false;
    }
  }

  tabVisibleOnExisting(tab) {
    switch (tab) {
      case 'summary':
        return true;

      case 'traveler-information':
        return this.isTripSelected;

      case 'dorm-room':
      case 'airfare':
        return this.isTripSelected && (!this.isNewTrip || this.policyAgreedTo);
      //      case 'confirmation':
      //return this.isTripSelected && (!this.isNewTrip || this.policyAgreedTo);

      case 'travel-policy':
      case 'confirmation':
      default:
        return false;
    }
  }

  tabVisible(tab) {
    return this.isNewTrip ? this.tabVisibleOnNew(tab) : this.tabVisibleOnExisting(tab);
  }

  questionAnswered(question, answer) {
    switch (question) {
      case 'policyAgreedTo':
      case 'needsDormRoom':
      case 'needsAirfare':
        this[question] = true === answer;
        break;

      default:
        break;
    }
  }

  async loadMentor() {
    this.mentor = null;
    const mentorId = (this.user || {}).mentorId || 0;

    if (!mentorId) {
      return;
    }

    try {
      this.mentor = await userDao.getUserById(mentorId);
    } catch (error) {
      this.$bvToast.toast(error.message, errorToastOptions);
    }
  }

  async reloadTrips() {
    try {
      this.trips = await travelDao.getTrips(this.user.id);
      const pendingTrips = await travelDao.getPendingTrips(this.user.id);

      pendingTrips.forEach((t) => {
        const idx = _(this.trips).findIndex((trip) => trip.registrationId === t.registrationId);

        if (0 <= idx) {
          this.trips.splice(idx, 1, t);
        }
      });

      this.trips.forEach((t) => {
        if (!!t.traveler && !!t.traveler.workspace) {
          t.traveler.workspace.nameConfirmed = 0 < t.id;
        }
      });
    } catch (error) {
      this.$bvToast.toast(error.message, errorToastOptions);
    }
  }

  rollback(includeTraveler = true, includeDorms = true, includeAirfare = true) {
    const trip = this.selectedTrip;

    if (trip instanceof Trip) {
      trip.rollback();

      if (!!trip.traveler && _(trip.traveler.rollback).isFunction() && includeTraveler) {
        trip.traveler.rollback();
      }

      if (includeDorms) {
        trip.dormStays = this.sDormStays.map(DormStay.create);

        this.$nextTick(() => {
          if (_((this.$refs.selectDormRoom || {}).setDays).isFunction()) {
            this.$refs.selectDormRoom.setDays(this.selectedTrip);
          }
        });
      }

      if (includeAirfare) {
        trip.flightRequests = {};

        if (this.sFlights.to) {
          trip.flightRequests.to = FlightRequest.create(this.sFlights.to);
        }

        if (this.sFlights.from) {
          trip.flightRequests.from = FlightRequest.create(this.sFlights.from);
        }

        this.$nextTick(() => {
          if (_((this.$refs.selectAirfare || {}).setFlights).isFunction()) {
            this.$refs.selectAirfare.setFlights();
          }
        });
      }
    }
  }

  @Watch('tabIndex')
  watchTabIndex() {
    this.$nextTick(() => {
      if (_((this.$refs.selectDormRoom || {}).setDays).isFunction()) {
        this.$refs.selectDormRoom.setDays(this.selectedTrip);
      }

      if (_((this.$refs.selectAirfare || {}).setFlights).isFunction()) {
        this.$refs.selectAirfare.setFlights();
      }
    });
  }

  @Watch('selectedTrip')
  watchSelectedTrip() {
    this.rollback();
  }

  @Watch('user')
  async watchUser() {
    await this.loadMentor();
  }

  get tabId() {
    const tabIds = ['summary', 'traveler-information', 'travel-policy', 'dorm-room', 'airfare', 'confirmation'];

    if (this.isNewTrip) {
      // Hide the summary tab
      tabIds.splice(0, 1);
    }

    if (!this.isNewTrip) {
      // We won't show the travel policy
      // when travel has already been selected
      tabIds.splice(2, 1);

      // And pop off the confirmation
      tabIds.pop();
    }

    return tabIds[this.tabIndex];
  }

  buttonVisible(button) {
    switch (button) {
      case 'start-over':
        return this.tabIndex > 1 && !!this.selectedTrip;

      case 'previous':
        return this.tabIndex > 0;

      case 'next':
        switch (this.tabId) {
          case 'traveler-information':
            try {
              return this.$refs.travelerInformationTab.isValid();
            } catch (ignore) {
              return true;
            }

          case 'travel-policy':
            // The trip must be selected
            return (
              this.isTripSelected &&
              // The policy must be agreed to
              this.policyAgreedTo &&
              // Both questions about dorm room and airfare must be answered
              null !== this.needsDormRoom &&
              null !== this.needsAirfare &&
              // At least one of dorm room or airfare was answered 'Yes'
              (this.needsDormRoom || this.needsAirfare)
            );
          case 'dorm-room':
            // The trip must be selected
            return (
              this.isTripSelected &&
              // The policy must be agreed to
              this.policyAgreedTo &&
              // If dorm rooms are being requested, make sure that dorm rooms have been selected
              null !== this.needsDormRoom &&
              (!this.needsDormRoom || !_(this.dormStays).isEmpty())
            );

          case 'airfare':
            // The trip must be selected
            return (
              this.isTripSelected &&
              // The policy must be agreed to
              this.policyAgreedTo &&
              // If dorm rooms are being requested, make sure the dorm rooms have been selected
              null !== this.needsDormRoom &&
              (!this.needsDormRoom || !_(this.dormStays).isEmpty()) &&
              // If airfare is requested, make sure flights have been selected
              null !== this.needsAirfare &&
              (!this.needsAirfare || !_(this.flightRequests).isEmpty()) &&
              // The traveler's name must be confirmed
              this.selectedTrip.traveler.workspace.confirmed &&
              // The traveler's DOB isn't in the future
              _(this.selectedTrip.traveler.workspace.dateOfBirth).isDate() &&
              isAfter(new Date(), this.selectedTrip.traveler.workspace.dateOfBirth)
            );

          case 'confirmation':
            return false;

          default:
            return false;
        }

      case 'decline-travel':
        return this.tabId === 'traveler-information' && this.isTripSelected;

      case 'accept':
        // The trip must be selected
        return (
          this.isTripSelected &&
          // The policy must be agreed to
          this.policyAgreedTo &&
          // At least one of needs dorm room or needs airfare has been selected
          (this.needsDormRoom || this.needsAirfare) &&
          // If dorm rooms are being requested, make sure the dorm rooms have been selected
          (!this.needsDormRoom || !_(this.dormStays).isEmpty()) &&
          // If airfare is requested, make sure flights have been selected
          (!this.needsAirfare || !_(this.flightRequests).isEmpty()) &&
          (!this.needsAirfare ||
            (this.selectedTrip.traveler.workspace.confirmed &&
              _(this.selectedTrip.traveler.workspace.dateOfBirth).isDate() &&
              isAfter(new Date(), this.selectedTrip.traveler.workspace.dateOfBirth))) &&
          // And we need to be on the last tab, so the 'next' button cannot be visible
          !this.buttonVisible('next')
        );

      default:
        return false;
    }
  }

  next() {
    this.$nextTick(() => {
      if (!this.buttonVisible('next')) {
        return this.$bvToast.toast('Please correct the highlighted errors before continuing.', errorToastOptions);
      }

      this.tabIndex++;
    });
  }

  previous() {
    this.tabIndex = Math.max(this.tabIndex - 1, 0);
  }

  async saveTrip() {
    const trip = this.selectedTrip;

    if (!trip) {
      return;
    }

    try {
      switch (this.tabIndex) {
        case 1:
          const traveler = (trip.traveler || {}).workspace;
          const travelerSaved = await travelDao.saveTravelerInfo(traveler.cloneTemplate());

          if (travelerSaved) {
            this.$bvToast.toast('Traveler Information has been saved!', {
              title: 'Success',
              solid: true,
              variant: 'success'
            });

            this.selectedTrip.traveler.commit();
          } else {
            this.$bvToast.toast('There was an error while saving Traveler Information', errorToastOptions);
          }
          break;

        case 2:
          if (this.$refs.selectDormRoom.hasErrors()) {
            throw new Error('Please correct the highlighted errors before saving.');
          }

          const dormStays = this.selectedTrip.dormStays.map((ds) => ds.cloneTemplate()).map((ds) => ({ ...ds, tripId: this.selectedTrip.id }));
          const dormsSaved = await travelDao.saveDormRooms(trip.id, dormStays);

          if (dormsSaved) {
            this.$bvToast.toast('Your Dorm Room Request has been updated!', {
              title: 'Success',
              solid: true,
              variant: 'success'
            });
          }
          break;

        case 3:
          const selectAirfare = this.$refs.selectAirfare;
          if (selectAirfare.hasError('any') || !selectAirfare.isValidSelection) {
            throw new Error('Please correct the highlighted errors before saving.');
          }

          const frs = {};

          if ((trip.flightRequests || {}).to instanceof FlightRequest) {
            frs.to = trip.flightRequests.to.cloneTemplate();
          }

          if ((trip.flightRequests || {}).from instanceof FlightRequest) {
            frs.from = trip.flightRequests.from.cloneTemplate();
          }

          const airfareSaved = await travelDao.saveAirfare(trip.id, frs);

          if (airfareSaved) {
            this.$bvToast.toast('Your Flight Requests have been updated!', {
              title: 'Success',
              solid: true,
              variant: 'success'
            });
          }
      }
    } catch (error) {
      this.$bvToast.toast(error.message, errorToastOptions);
    }
  }

  async cancelTrip() {
    const trip = this.selectedTrip;

    if (!trip) {
      return;
    }

    try {
      const doCancel = await this.$bvModal.msgBoxConfirm('Are you sure you want to cancel this travel request?', {
        title: 'Travel Request Cancellation Confirmation',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelVariant: 'success',
        cancelTitle: 'No',
        centered: true
      });

      if (!doCancel) {
        return;
      }

      const canceled = await travelDao.cancelTrip(trip);

      if (canceled) {
        await this.reloadTrips();
        this.selectedTrip = this.trips.find((t) => t.registrationId === trip.registrationId);
        this.tabIndex = 0;
      }
    } catch (error) {
      this.$bvToast.toast(error.message, errorToastOptions);
    }
  }

  async acceptTravel() {
    const trip = this.selectedTrip.cloneTemplate();
    trip.traveler = this.selectedTrip.traveler.workspace.cloneTemplate();

    // Since dormStays is a computed property, upon which the code making the exceptional state
    // determination hinges, we cannot cache the exceptional state at the time of DormStay
    // object creation (hasErrors() calls into dormStays() which then calls into hasErrors() - too much recursion!)
    // we must map the exceptional state to the payload here
    trip.dormStays = trip.dormStays.map((ds) => ({
      ...ds,
      additionalRequests: this.$refs.selectDormRoom.additionalRequests,
      exception: this.dormException
    }));

    try {
      const savedTrip = await travelDao.saveTrip(trip);
      const idx = _(this.trips).findIndex((t) => t.registrationId === savedTrip.registrationId);
      this.trips.splice(idx, 1, savedTrip);

      this.selectedTrip = null;

      this.$nextTick(() => {
        this.selectedTrip = savedTrip;
        savedTrip.traveler.workspace.nameConfirmed = true;
        setTimeout(() => (this.tabIndex = 0), 50);
      });
    } catch (error) {
      await this.$bvModal.msgBoxOk(error.message, errorModalOptions);
    }
  }

  async declineTravel() {
    const check = await this.$bvModal.msgBoxConfirm('Are you sure you want to decline travel?', {
      title: 'Confirm',
      noCloseOnBackdrop: true,
      noCloseOnEsc: true,
      headerBgVariant: 'dark',
      headerTextVariant: 'white'
    });

    if (!check) {
      return;
    }

    try {
      const registrationId = this.selectedTrip.registrationId;
      const declinedTravel = true;
      await this.$store.dispatch('registration/declinedTravel', { registrationId, declinedTravel }, { root: true });

      await this.reloadTrips();

      this.selectedTrip = null;

      this.$nextTick(() => (this.selectedTrip = _(this.trips).find((t) => t.registrationId === registrationId)));
    } catch (error) {
      await this.$bvModal.msgBoxOk(error.message, errorModalOptions);
    }
  }

  async undeclineTravel() {
    try {
      const registrationId = this.selectedTrip.registrationId;
      await travelDao.undeclineTravel(registrationId);

      await this.reloadTrips();

      this.selectedTrip = null;

      this.$nextTick(() => {
        this.selectedTrip = _(this.trips).find((t) => t.registrationId === registrationId);

        this.$bvToast.toast('Travel requests for this registration are enabled again.', {
          title: 'Success',
          solid: true,
          variant: 'success'
        });
      });
    } catch (error) {
      await this.$bvModal.msgBoxOk(error.message, errorModalOptions);
    }
  }

  async mounted() {
    this.$store.commit('setBreadcrumbs', [
      Breadcrumb.create('Administration', { name: 'adminMain' }),
      Breadcrumb.create('My Travel', { name: 'myTravel' })
    ]);

    try {
      await Promise.all(['travel/loadAirports', 'travel/loadPolicy', 'common/loadStates'].map((key) => this.$store.dispatch(key)));
    } catch (error) {
      this.$bvToast.toast(error.message, errorToastOptions);
    }

    await Promise.all([this.reloadTrips(), this.loadMentor()]);

    this.selectedTrip = _(this.trips).find((t) => t.registrationId === this.registrationId);
  }
}
</script>
<style scoped>
#tab-id-provider {
  display: none;
}
</style>
